import { useCallback, useEffect, useState, type RefObject } from 'react';

export interface FullscreenControl {
  /** True while the tracked element is the one filling the screen. */
  active: boolean;
  /** False on iOS Safari, which only allows fullscreen on <video> elements. */
  supported: boolean;
  toggle: () => void;
}

/**
 * Fullscreen for a container rather than a single element.
 *
 * A fullscreen element paints only itself and its descendants, so whatever is
 * made fullscreen has to be the whole composition the user expects to keep —
 * here the stage, which owns the video, the filmstrip and the floating control
 * bar. Targeting the video tile alone takes the picture fullscreen and the
 * controls off the screen with it.
 */
export function useFullscreen(ref: RefObject<HTMLElement | null>): FullscreenControl {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const sync = () => setActive(document.fullscreenElement === ref.current);
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [ref]);

  const toggle = useCallback(() => {
    // Exiting is document-wide: if something else took over the screen, the
    // honest response to "leave fullscreen" is still to leave it.
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    void ref.current?.requestFullscreen().catch(() => undefined);
  }, [ref]);

  return {
    active,
    supported: typeof document !== 'undefined' && document.fullscreenEnabled,
    toggle,
  };
}
